import type { ConsentType } from "./hooks"

// Telehealth consent copy for the consumer booking flow (MEM-755).
//
// Literal strings (no react-intl): this package isn't in the intl extraction pipeline and the
// experience is English-only for the foreseeable future. Keyed by consent type so each consent's copy
// is unambiguous.
//
// ⚠ This is legal consent copy — change it deliberately. Consent signoffs are recorded with only a
// timestamp (no stored version), so "what a member agreed to" is reconstructed from this file's
// git/deploy history at their signedAt. If Legal/Bridge ever require an explicit version, reintroduce
// one here + on the signConsent payload + in the stored record (see MEM-756).

type ConsentEntry = {
  // The read-only copy section the member reads for this consent.
  section: { heading: string; body: string }
  // The checkbox that acknowledges it (ConsentPage keys the form field by consent type).
  clause: { label: string; requiredMessage: string }
}

export type ConsentDocument = {
  title: string
  intro: string
  // Every consent type must have copy — a missing one fails to compile.
  consents: Record<ConsentType, ConsentEntry>
  // The typed-name signature ratifies both consents (a shared ceremony, not its own consent). Per
  // MEM-755 the typed name is client-side gating only — never sent or stored. `attestation` is the
  // legally-required lead-in above the field; `helper` sits below it; `signedOnLabel` builds the
  // "Signed on {date}" line.
  signature: {
    attestation: string
    label: string
    helper: string
    requiredMessage: string
  }
}

export const consentDocument: ConsentDocument = {
  title: "Consent to telehealth services",
  intro: "Please read and agree to the following before booking your appointment.",
  consents: {
    telehealth_informed_consent: {
      section: {
        heading: "Telehealth visit",
        body: "Your appointment will take place over a secure HIPAA-compliant video call. By agreeing, you consent to receiving care through this format and understand that your provider may determine an in-person visit is more appropriate for your needs."
      },
      clause: {
        label:
          "I consent to a telehealth visit and understand that care is delivered by independent, board-certified clinicians.",
        requiredMessage: "Please confirm your consent to a telehealth visit."
      }
    },
    // PLACEHOLDER copy (MEM-756 interim, flag-gated/stage-only). NPP and HIPAA get their real copy +
    // per-consent page layout in the consent-UI ticket; this is throwaway text so the persistence loop
    // (establish patient → record seen/signed → gate flips) is testable end-to-end now.
    notice_of_privacy_practices: {
      section: {
        heading: "Notice of Privacy Practices — placeholder",
        body: "PLACEHOLDER — final copy pending the consent UI ticket."
      },
      clause: {
        label: "[Placeholder] I acknowledge I have received the Notice of Privacy Practices.",
        requiredMessage: "Please acknowledge the Notice of Privacy Practices."
      }
    },
    hipaa: {
      section: {
        heading: "HIPAA authorization (optional) — placeholder",
        body: "PLACEHOLDER — final copy pending the consent UI ticket. This authorization is optional; you can continue without it. Lorem ipsum dolor sit amet, consectetur adipiscing elit."
      },
      clause: {
        label: "[Placeholder] I authorize Carrot to share my health information (optional).",
        // Optional consent — never gates submit, so this message is unused today; kept for shape parity.
        requiredMessage: "Optional."
      }
    }
  },
  signature: {
    // Legal-required lead-in above the field (per the FigJam legal note). Final wording pends legal
    // sign-off; this mirrors their drafted language.
    attestation:
      "By typing my name below, I confirm that I have read and agree to the telehealth informed consent and consent to treat above, and I voluntarily consent to receive care from Carrot.",
    label: "Type your full name to sign",
    helper: "Typing your name here serves as your electronic signature.",
    requiredMessage: "Please type your full name to sign."
  }
}

// Every consent type the flow knows about — the keys of `consents`, so it can't drift from the copy.
// Used as the safe fallback when the server's per-consent status is unavailable: treat all as
// outstanding so the member can still sign. The cast is sound because `consents` is keyed by
// ConsentType (a missing key wouldn't compile).
export const consentTypes = Object.keys(consentDocument.consents) as ConsentType[]
